import React from 'react';
import Box from '../../atoms/Box';
import Text from '../../atoms/Text';
import {
  formatTooltipValue,
  type FormatTooltipValueOptions,
} from './formatTooltipValue';

export interface ChartTooltipItem {
  label?: React.ReactNode;
  value: number | string | null | undefined;
  color?: string;
}

interface ChartTooltipRootProps {
  title?: React.ReactNode;
  items?: ChartTooltipItem[];
  unit?: string;
  decimals?: FormatTooltipValueOptions['decimals'];
  locale?: FormatTooltipValueOptions['locale'];
  showTotal?: boolean;
  totalLabel?: string;
  showPercentage?: boolean;
  // when set, percentages divide by this fixed total instead of summing items
  // useful for pie/donut where only one slice is hovered
  percentageBase?: number;
  accentColor?: string;

  children?: React.ReactNode;
}

function Root({
  title,
  items,
  unit,
  decimals,
  locale,
  showTotal = false,
  totalLabel = 'Total',
  showPercentage = false,
  percentageBase,
  accentColor,
  children,
}: ChartTooltipRootProps) {
  const autoItems = Array.isArray(items) && items.length > 0 ? items : null;
  const hasContent = autoItems !== null || !!children || title !== undefined;
  if (!hasContent) return null;

  const numericTotal = autoItems
    ? autoItems.reduce<number>((sum, item) => {
        const num =
          typeof item.value === 'number' ? item.value : Number(item.value);
        return Number.isFinite(num) ? sum + num : sum;
      }, 0)
    : 0;

  const percentageDenominator =
    typeof percentageBase === 'number' && Number.isFinite(percentageBase)
      ? percentageBase
      : numericTotal;

  return (
    <Box
      sx={{
        background: 'rgba(255, 255, 255, 0.98)',
        borderRadius: '2px',
        border: '1px solid #e2e8f0',
        ...(accentColor ? { borderLeft: `3px solid ${accentColor}` } : {}),
        padding: '12px 16px',
        minWidth: '180px',
      }}
    >
      {title !== undefined && title !== null && title !== '' && (
        <Time>{title}</Time>
      )}

      {autoItems && (
        <Section>
          {autoItems.map((item, index) => {
            const numeric =
              typeof item.value === 'number' ? item.value : Number(item.value);
            const percentage =
              showPercentage &&
              percentageDenominator > 0 &&
              Number.isFinite(numeric)
                ? ` (${((numeric / percentageDenominator) * 100).toFixed(1)}%)`
                : '';
            const formatted = formatTooltipValue(item.value, {
              decimals,
              locale,
              unit,
            });
            return (
              <Row
                key={index}
                label={item.label ?? undefined}
                value={`${formatted}${percentage}`}
                markColor={item.color}
              />
            );
          })}
        </Section>
      )}

      {showTotal && autoItems && (
        <Section>
          <Row
            label={totalLabel}
            value={formatTooltipValue(numericTotal, {
              decimals,
              locale,
              unit,
            })}
            large
          />
        </Section>
      )}

      {children}
    </Box>
  );
}

function Time({ children }: { children: React.ReactNode }) {
  return (
    <Text
      sx={{
        fontWeight: 600,
        fontSize: '0.9rem',
        margin: '0 0 8px 0',
        color: '#2d3748',
        borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
        paddingBottom: '5px',
        display: 'block',
      }}
    >
      {children}
    </Text>
  );
}

function Section({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ marginBottom: '8px', '&:last-child': { marginBottom: 0 } }}>
      {children}
    </Box>
  );
}

function Row({
  label,
  value,
  valueColor,
  markColor,
  large = false,
  noLabel = false,
}: {
  label?: React.ReactNode;
  value: React.ReactNode;
  valueColor?: string;
  markColor?: string;
  large?: boolean;
  noLabel?: boolean;
}) {
  const showColon = !noLabel && !markColor;
  return (
    <Box
      sx={{
        margin: '3px 0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
      }}
    >
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          minWidth: 0,
        }}
      >
        {markColor && (
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              width: '12px',
              height: '3px',
              borderRadius: '1px',
              backgroundColor: markColor,
              flexShrink: 0,
            }}
          />
        )}
        {label ? (
          <Text
            component="span"
            sx={{
              fontSize: '0.85rem',
              color: '#718096',
              ...(showColon ? { '&::after': { content: '":"' } } : {}),
            }}
          >
            {label}
          </Text>
        ) : (
          <span />
        )}
      </Box>

      <Text
        component="span"
        sx={{
          fontSize: large ? '0.95rem' : '0.85rem',
          fontWeight: 600,
          color: valueColor ?? '#2d3748',
          overflowWrap: 'anywhere',
        }}
      >
        {value}
      </Text>
    </Box>
  );
}

const ChartTooltip = Object.assign(Root, {
  Time,
  Section,
  Row,
});

export { formatTooltipValue };
export type { FormatTooltipValueOptions };
export default ChartTooltip;
